export const THIRD_PARTY_RESPONSIBLE_SEPARATOR = '|||RS_TP|||';
export const THIRD_PARTY_RESPONSIBLE_MAX_LENGTH = 255;
export const THIRD_PARTY_RESPONSIBLE_MAX_LINKS = 100;

const stableUnique = (values: string[]): string[] => {
  const seen = new Set<string>();

  return values.filter((value) => {
    if (seen.has(value)) {
      return false;
    }

    seen.add(value);

    return true;
  });
};

export const parseThirdPartyResponsible = (
  value: string | null | undefined
): string[] => {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  if (!value.includes(THIRD_PARTY_RESPONSIBLE_SEPARATOR)) {
    return [value];
  }

  return stableUnique(
    value
      .split(THIRD_PARTY_RESPONSIBLE_SEPARATOR)
      .map((name) => name.trim())
      .filter(Boolean)
  );
};

const areEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

/**
 * Encodes typed responsible Third Party names into the existing scalar field.
 * Passing the originally-loaded scalar preserves untouched legacy values
 * byte-for-byte, including whitespace that predates the multi-value codec.
 */
export const serializeThirdPartyResponsible = (
  names: string[],
  originalValue?: string | null
): string | null => {
  if (
    originalValue !== null &&
    originalValue !== undefined &&
    areEqual(names, parseThirdPartyResponsible(originalValue))
  ) {
    return originalValue;
  }

  const normalisedNames = stableUnique(
    names.map((name) => name.trim()).filter(Boolean)
  );

  if (
    normalisedNames.some((name) =>
      name.includes(THIRD_PARTY_RESPONSIBLE_SEPARATOR)
    )
  ) {
    throw new Error(
      `A third party name cannot contain ${THIRD_PARTY_RESPONSIBLE_SEPARATOR}`
    );
  }

  if (normalisedNames.length === 0) {
    return null;
  }

  const encoded = normalisedNames.join(THIRD_PARTY_RESPONSIBLE_SEPARATOR);
  if (encoded.length > THIRD_PARTY_RESPONSIBLE_MAX_LENGTH) {
    throw new Error(
      `Third party names must be ${THIRD_PARTY_RESPONSIBLE_MAX_LENGTH} characters or fewer in total`
    );
  }

  return encoded;
};

/**
 * Accepts delimiter-free legacy values unchanged, while requiring new
 * delimiter-bearing values to match the codec's canonical wire format.
 */
export const isValidThirdPartyResponsibleScalar = (
  value: string | null | undefined
): boolean => {
  if (!value?.includes(THIRD_PARTY_RESPONSIBLE_SEPARATOR)) {
    return true;
  }

  try {
    return (
      serializeThirdPartyResponsible(parseThirdPartyResponsible(value)) ===
      value
    );
  } catch {
    return false;
  }
};
